/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";
import { sanitizeUrl } from "@braintree/sanitize-url";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const ModelCtor = (typeof globalThis !== "undefined" && (globalThis.ModelNode || globalThis.Model)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

// Clone a single A-Frame component attribute from one entity to another.
// Minimal + resilient to empty-string or object-valued components.
function cloneAFrameComponentIfPresent(srcEl, dstEl, name) {
  if (!HAS_AFRAME || !srcEl || !dstEl) return;
  if (!srcEl.hasAttribute(name)) return;

  const data = srcEl.getAttribute(name);
  if (data == null || data === "" || (typeof data === "object" && !Object.keys(data).length)) {
    // attribute exists but has no payload (e.g. loop-animation="")
    dstEl.setAttribute(name, "");
  } else if (typeof data === "object") {
    // avoid sharing references
    dstEl.setAttribute(name, { ...data });
  } else {
    // string form (or anything else)
    dstEl.setAttribute(name, data);
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const MODEL_PROPS = [
  "src",
  "castShadow",
  "receiveShadow",
  "billboard",
  "collidable",
  "walkable",
  "enableOnProximity",
  "proximityRadius",
  "activeClipItems",
  "enableMaterialColor",
  "materialColor",
  "emissiveColor",
  "networkable"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class ModelEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    MODEL_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else if ((prop === "materialColor" || prop === "emissiveColor") && sourceObject?.[prop]?.set) {
            sourceObject[prop].set(v);
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class ModelAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const CMP = "scriptable-model";
    const pending = (this._pending = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      const cur = { ...(el.getAttribute(CMP) || {}) };
      Object.assign(cur, pending);
      Object.keys(pending).forEach(k => delete pending[k]);
      el.setAttribute(CMP, cur);
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      if (e?.detail?.name === CMP) flush();
    });

    const setCmpProp = (k, v) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(CMP);
      if (!attr) {
        pending[k] = v;
        return;
      }
      const cur = { ...attr, [k]: v };
      el.setAttribute(CMP, cur);
    };
    const getCmpProp = k => {
      const el = this.sourceObject?.el;
      if (!el) return pending[k];
      const attr = el.getAttribute(CMP);
      return (attr && attr[k]) ?? pending[k];
    };

    Object.defineProperty(this, "src", {
      get: () => getCmpProp("src"),
      set(v) {
        setCmpProp("src", sanitizeUrl(v || ""));
      }
    });

    Object.defineProperty(this, "enableOnProximity", {
      get: () => !!getCmpProp("enableOnProximity"),
      set(v) {
        setCmpProp("enableOnProximity", !!v);
      }
    });

    Object.defineProperty(this, "proximityRadius", {
      get: () => getCmpProp("proximityRadius"),
      set(v) {
        setCmpProp("proximityRadius", v);
      }
    });

    Object.defineProperty(this, "enableMaterialColor", {
      get: () => !!getCmpProp("enableMaterialColor"),
      set(v) {
        setCmpProp("enableMaterialColor", !!v);
      }
    });

    Object.defineProperty(this, "materialColor", {
      get: () => getCmpProp("materialColor"),
      set(v) {
        setCmpProp("materialColor", v);
      }
    });

    Object.defineProperty(this, "emissiveColor", {
      get: () => getCmpProp("emissiveColor"),
      set(v) {
        setCmpProp("emissiveColor", v);
      }
    });

    Object.defineProperty(this, "activeClipItems", {
      get: () => getCmpProp("activeClipItems"),
      set(v) {
        setCmpProp("activeClipItems", Array.isArray(v) ? v.slice() : v);
      }
    });

    Object.defineProperty(this, "billboard", {
      get: () => !!getCmpProp("billboard"),
      set(v) {
        setCmpProp("billboard", !!v);
      }
    });

    // shadow component --------------------------------------------------------
    const setShadow = (key, val) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const cur = { ...(el.getAttribute("shadow") || {}) };
      cur[key] = !!val;
      el.setAttribute("shadow", cur);
    };
    const getShadow = key => this.sourceObject?.el?.getAttribute("shadow")?.[key];

    Object.defineProperty(this, "castShadow", {
      get: () => getShadow("cast"),
      set(v) {
        setShadow("cast", v);
      }
    });

    Object.defineProperty(this, "receiveShadow", {
      get: () => !!getShadow("receive"), // Added !! to ensure boolean return
      set(v) {
        setShadow("receive", v);
      }
    });

    Object.defineProperty(this, "collidable", {
      get: () => !!getCmpProp("collidable"),
      set(v) {
        setCmpProp("collidable", !!v);
      }
    });

    Object.defineProperty(this, "walkable", {
      get: () => !!getCmpProp("walkable"),
      set(v) {
        setCmpProp("walkable", !!v);
      }
    });

    // networked flag ----------------------------------------------------------
    Object.defineProperty(this, "networkable", {
      get: () => !!this.sourceObject?.el?.hasAttribute("networked"),
      set: v => {
        const el = this.sourceObject?.el;
        if (!el) return;
        if (v) {
          if (!el.hasAttribute("networked")) el.setAttribute("networked", { template: "#static-media" });
        } else {
          el.removeAttribute("networked");
        }
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of MODEL_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new ModelAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new ModelEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class ModelElement extends ScriptableObject {
  static scriptableType = "model";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      // birth the component with sane defaults
      el.setAttribute("scriptable-model", {
        src: sanitizeUrl(initial.src || ""),
        castShadow: !!initial.castShadow,
        receiveShadow: !!initial.receiveShadow,
        billboard: !!initial.billboard,
        collidable: !!initial.collidable,
        walkable: !!initial.walkable,
        enableOnProximity: !!initial.enableOnProximity,
        proximityRadius: initial.proximityRadius != null ? initial.proximityRadius : 3,
        enableMaterialColor: !!initial.enableMaterialColor,
        materialColor: initial.materialColor || "#ffffff",
        emissiveColor: initial.emissiveColor || "#000000",
        activeClipItems: initial.activeClipItems || [],
        networkable: !!initial.networkable
      });

      if (initial.networkable) {
        el.setAttribute("networked", { template: "#static-media" });
      }

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      if (!obj.scriptableType) {
        Object.defineProperty(obj, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }
      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createModelElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true, { preserveNetworked = true } = {}) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public model props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if ((prop === "materialColor" || prop === "emissiveColor") && val?.isColor) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of MODEL_PROPS) {
      if (!preserveNetworked && p === "networkable") continue;
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    // 5) A-Frame only: copy loop-animation component if present
    if (HAS_AFRAME && this.el && source?.el) {
      cloneAFrameComponentIfPresent(source.el, this.el, "loop-animation");
    }

    return this;
  }

  clone(recursive = true, { preserveNetworked = true } = {}) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of MODEL_PROPS) {
      if (!preserveNetworked && p === "networkable") continue;
      const v = this[p];
      if (v === undefined) continue;

      if ((p === "materialColor" || p === "emissiveColor") && v?.isColor) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor = this.constructor && typeof this.constructor.create === "function" ? this.constructor : ModelElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new ModelElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive, { preserveNetworked });
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor ModelNode instance to Scriptable, attach adapter and proxies
function makeEditorModelNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || ModelCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: ModelElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = ModelElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new ModelEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new ModelEditorAdapter(node, descriptors);
  }

  for (const prop of MODEL_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}


/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
MODEL_PROPS.forEach(prop => {
  Object.defineProperty(ModelElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame animation helpers (optional, parity with your old API)             */
/* ────────────────────────────────────────────────────────────────────────── */
if (HAS_AFRAME && !Object.getOwnPropertyDescriptor(ModelElement.prototype, "animationMixer")) {
  Object.defineProperty(ModelElement.prototype, "animationMixer", {
    get() {
      return this.el?.components?.["animation-mixer"]?.mixer || null;
    },
    enumerable: true,
    configurable: true
  });
}

if (HAS_AFRAME && !Object.getOwnPropertyDescriptor(ModelElement.prototype, "animations")) {
  Object.defineProperty(ModelElement.prototype, "animations", {
    get() {
      const cmp = this.el?.components?.["animation-mixer"];
      if (Array.isArray(cmp?.animations)) return cmp.animations;
      if (Array.isArray(this.el?.object3D?.animations)) return this.el.object3D.animations;
      return [];
    },
    set(v) {
      if (!Array.isArray(v)) return;
      const cmp = this.el?.components?.["animation-mixer"];
      if (cmp?.initMixer) cmp.initMixer(v);
      else if (this.el?.object3D) this.el.object3D.animations = v;
    },
    enumerable: true,
    configurable: true
  });
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createModelElementEditor(initial = {}) {
  const ModelCtor = globalThis.ModelNode;

  if (typeof ModelCtor !== "function") {
    throw new Error("ModelElementEditor: ModelNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new ModelCtor(editor);
  } catch (e) {
    throw new Error(`ModelElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorModelNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("model", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== ModelElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return ModelElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorModelNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(ModelElement.prototype, Symbol.toStringTag, { value: "ModelElement" });
